import { useEffect, useMemo, useRef } from "react";
import {
  getTimezoneOptionsAll,
  getTimezoneOptionsForCountry,
  getLocalTimeZone,
  iso2FromCountryName,
  suggestZoneByState,
  type TimezoneOption,
} from "../utils/timezones";
import type { UseFormSetValue } from "react-hook-form";
import type { FormValues } from "../features/customer/AddCustomerForm/schema";

export function useTimezone(
  companyCountryId: number | undefined,
  companyStateId: number | undefined,
  timezoneValue: string,
  setValue: UseFormSetValue<FormValues>,
  countries: Array<{ id: number; name: string }>,
  states: Array<{ id: number; name: string }>
) {
  const didInitRef = useRef(false);
  const autoSetKindRef = useRef<"none" | "local" | "suggested">("none");

  const selectedCompanyCountry = useMemo(
    () => countries.find((c) => c.id === Number(companyCountryId)),
    [countries, companyCountryId]
  );

  const selectedCompanyIso2 = useMemo(
    () => iso2FromCountryName(selectedCompanyCountry?.name) || null,
    [selectedCompanyCountry]
  );

  const selectedCompanyState = useMemo(
    () => states.find((s) => s.id === Number(companyStateId)),
    [states, companyStateId]
  );

  const tzOptions: TimezoneOption[] = useMemo(() => {
    if (selectedCompanyIso2)
      return getTimezoneOptionsForCountry(selectedCompanyIso2);
    return getTimezoneOptionsAll();
  }, [selectedCompanyIso2]);

  const suggestedTimezone = useMemo(() => {
    if (selectedCompanyIso2 && selectedCompanyState?.name) {
      return suggestZoneByState(selectedCompanyIso2, selectedCompanyState.name);
    }
    return null;
  }, [selectedCompanyIso2, selectedCompanyState]);

  const localTimezone = useMemo(() => getLocalTimeZone(), []);

  // 1) Set local timezone once on first mount if empty
  useEffect(() => {
    if (!didInitRef.current && !timezoneValue && localTimezone) {
      setValue("timezone", localTimezone, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
      autoSetKindRef.current = "local";
      didInitRef.current = true;
    }
  }, [timezoneValue, localTimezone, setValue]);

  // 2) If user picks a state, auto-suggest ONLY when empty
  useEffect(() => {
    if (
      suggestedTimezone &&
      !timezoneValue &&
      tzOptions.some((o) => o.value === suggestedTimezone)
    ) {
      setValue("timezone", suggestedTimezone, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
      autoSetKindRef.current = "suggested";
    }
  }, [suggestedTimezone, timezoneValue, tzOptions, setValue]);

  // 3) If current timezone is not available under the chosen country
  useEffect(() => {
    if (timezoneValue && tzOptions.length > 0) {
      const exists = tzOptions.some((o) => o.value === timezoneValue);
      if (!exists && autoSetKindRef.current !== "local") {
        setValue("timezone", "", {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
        autoSetKindRef.current = "none";
      }
    }
  }, [tzOptions, timezoneValue, setValue]);

  return { tzOptions, suggestedTimezone, localTimezone };
}
